import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-project-detail-tasks',
  templateUrl: './project-detail-tasks.component.html',
  styleUrls: ['./project-detail-tasks.component.scss']
})
export class ProjectDetailTasksComponent implements OnInit, OnChanges {
  @Input() projectId: any;
  @Input() selectedItem: any = null;
  @Input() projectMembers: any[] = [];
  @Input() currentUserId: any;
  @Input() sprints: any[] = [];
  @Input() modules: any[] = [];

  @Output() taskSelected = new EventEmitter<any>();
  @Output() taskEdit = new EventEmitter<any>();
  @Output() taskDelete = new EventEmitter<{ task: any; event: Event }>();
  @Output() addTaskClicked = new EventEmitter<void>();
  @Output() taskStatusToggled = new EventEmitter<any>();

  allProjectTasks: any[] = [];
  loadingAllTasks = false;
  totalRows = 0;

  taskSearchQuery = '';
  priorityFilter = 'all';
  statusFilter = 'all';
  assigneeFilter = 'all';
  sprintFilter = 'all';
  moduleFilter = 'all';
  onlyMyTasks = false;
  onlyOverdue = false;

  pageIndex = 0;
  pageSize = 20;
  pageSizeOptions = [10, 25, 50, 100];

  loadingMore = false;
  hasMore = true;

  statusGroupsList = ['todo', 'in_progress', 'in_review', 'completed', 'blocked', 'cancelled', 'other'];

  expandedStatusGroups: { [key: string]: boolean } = {
    todo: true,
    in_progress: true,
    in_review: true,
    completed: true,
    blocked: true,
    cancelled: false,
    other: true
  };

  constructor(
    private taskService: ProjectTaskService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.loadTasks();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.pageIndex = 0;
      this.loadTasks();
    }
  }

  loadTasks(append: boolean = false) {
    if (!append) {
      this.pageIndex = 0;
      this.allProjectTasks = [];
      this.loadingAllTasks = true;
    } else {
      this.loadingMore = true;
    }

    const whereClause: any = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId()
    };

    if (this.priorityFilter && this.priorityFilter !== 'all') {
      whereClause.priority = this.priorityFilter;
    }

    if (this.statusFilter && this.statusFilter !== 'all') {
      whereClause.status = this.statusFilter;
    }

    if (this.assigneeFilter && this.assigneeFilter !== 'all') {
      whereClause.assignee_id = this.assigneeFilter;
    }

    if (this.sprintFilter && this.sprintFilter !== 'all') {
      if (this.sprintFilter === 'backlog') {
        whereClause.sprint_id = null;
      } else {
        whereClause.sprint_id = Number(this.sprintFilter);
      }
    }

    if (this.moduleFilter && this.moduleFilter !== 'all') {
      whereClause.module_id = Number(this.moduleFilter);
    }

    if (this.onlyMyTasks && this.currentUserId) {
      whereClause.assignee_id = this.currentUserId;
    }

    const payload: any = {
      page: this.pageIndex + 1, // 1-based page index on task controller
      limit: this.pageSize,
      user_id: this.localStorage.getUserId(),
      where: whereClause,
      order: {
        col: 'updated_at',
        dir: 'DESC'
      }
    };

    if (this.taskSearchQuery?.trim()) {
      payload.search = this.taskSearchQuery.trim();
    }

    this.taskService.getAllProjectTasks(payload).subscribe({
      next: (res: any) => {
        const rows = res?.rows || res?.tasks || res?.data?.rows || res?.data || [];
        this.totalRows = res?.total || res?.count || res?.totalCount || res?.data?.total || 0;

        let processedRows = rows;
        if (this.onlyOverdue) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          processedRows = rows.filter((t: any) => t.due_date && new Date(t.due_date) < today && t.status !== 'completed');
        }

        if (append) {
          this.allProjectTasks = [...this.allProjectTasks, ...processedRows];
        } else {
          this.allProjectTasks = processedRows;
        }

        this.hasMore = this.allProjectTasks.length < this.totalRows;
        this.loadingAllTasks = false;
        this.loadingMore = false;
      },
      error: () => {
        this.loadingAllTasks = false;
        this.loadingMore = false;
        this.toastr.error('Failed to load tasks');
      }
    });
  }

  onFilterChange() {
    this.pageIndex = 0;
    this.loadTasks(false);
  }

  onTableScroll(event: any) {
    const el = event.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      if (!this.loadingAllTasks && !this.loadingMore && this.hasMore) {
        this.pageIndex++;
        this.loadTasks(true);
      }
    }
  }

  normalizeStatusKey(s: any): string {
    if (!s) return '';
    return String(s).toLowerCase().replace(/[\s_-]+/g, '');
  }

  getTasksByStatus(statusGroupKey: string) {
    const targetKey = this.normalizeStatusKey(statusGroupKey);
    const tasks = this.allProjectTasks || [];

    if (targetKey === 'other') {
      const known = ['todo', 'inprogress', 'inreview', 'codereview', 'completed', 'done', 'blocked', 'cancelled'];
      return tasks.filter(t => !known.includes(this.normalizeStatusKey(t.status || t.project_task_status)));
    }

    return tasks.filter(t => {
      const k = this.normalizeStatusKey(t.status || t.project_task_status);
      if (targetKey === 'todo') return k === 'todo';
      if (targetKey === 'inprogress') return k === 'inprogress';
      if (targetKey === 'inreview') return k === 'inreview' || k === 'codereview';
      if (targetKey === 'completed') return k === 'completed' || k === 'done';
      if (targetKey === 'blocked') return k === 'blocked';
      if (targetKey === 'cancelled') return k === 'cancelled';
      return k === targetKey;
    });
  }

  getStatusPillClass(status: string): string {
    const k = this.normalizeStatusKey(status);
    if (k === 'todo') return 'status-pill--todo';
    if (k === 'inprogress') return 'status-pill--in_progress';
    if (k === 'inreview' || k === 'codereview') return 'status-pill--in_review';
    if (k === 'completed' || k === 'done') return 'status-pill--completed';
    if (k === 'blocked') return 'status-pill--blocked';
    if (k === 'cancelled') return 'status-pill--cancelled';
    return 'status-pill--todo';
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'todo': return 'To Do';
      case 'in_progress': return 'In Progress';
      case 'in_review': return 'In Review';
      case 'completed': return 'Completed';
      case 'blocked': return 'Blocked';
      case 'cancelled': return 'Cancelled';
      default: return this.prettyLabel(status);
    }
  }

  prettyLabel(val: string): string {
    if (!val) return '';
    return val.replace(/_/g, ' ');
  }

  getPriorityClass(priority: string): string {
    if (!priority) return 'priority-low';
    return `priority-${priority.toLowerCase()}`;
  }

  getAvatarColor(user: any): string {
    if (!user || !user.fname) return '#6366f1';
    const colors = ['#ec4899', '#f43f5e', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];
    const idx = (user.fname.charCodeAt(0) + (user.lname?.charCodeAt(0) || 0)) % colors.length;
    return colors[idx];
  }

  getUserObject(userRef: any): any {
    if (!userRef) return null;
    if (typeof userRef === 'object' && (userRef.fname || userRef.lname || userRef.first_name)) {
      return userRef;
    }
    const uid = Number(userRef);
    if (!uid) return null;
    return (this.projectMembers || []).find((m) => Number(m.user_id) === uid) || null;
  }

  getReporterUser(task: any): any {
    if (!task) return null;
    return (
      this.getUserObject(task.reporter) ||
      this.getUserObject(task.reporter_id) ||
      this.getUserObject(task.created_by_user) ||
      this.getUserObject(task.created_by) ||
      null
    );
  }

  getInitials(user: any): string {
    if (!user) return '?';
    const f = user.fname ? user.fname.charAt(0).toUpperCase() : '';
    const l = user.lname ? user.lname.charAt(0).toUpperCase() : '';
    return f + l;
  }

  toggleStatusGroup(status: string) {
    this.expandedStatusGroups[status] = !this.expandedStatusGroups[status];
  }

  updateTaskStatus(task: any, newStatus: string) {
    const prev = task.status;
    task.status = newStatus;
    this.taskService
      .updateProjectTask({
        task_id: task.task_id,
        where: { task_id: task.task_id },
        status: newStatus,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        next: () => {
          this.toastr.success('Task status updated successfully');
          this.loadTasks();
        },
        error: () => {
          task.status = prev;
          this.toastr.error('Failed to update task status');
        },
      });
  }
}
